import { useEffect, useState } from "react";
import { supabase, isSupabaseConfigured } from "./lib/supabase";
import { recordSignIn } from "./lib/data";
import LoginScreen from "./LoginScreen";
import App from "./App";
import InvestorView from "./InvestorView";
import ScenarioView from "./ScenarioView";
import SetPassword from "./SetPassword";

const T = { bg: "#F2EBDC", muted: "#7A7468", cost: "#8C2A2A" };

function Message({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Manrope', system-ui, sans-serif",
        color: T.muted,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

/**
 * v2 has no local mode. Every figure has to trace back to a real document, and
 * a localStorage sandbox would be a second source of truth by another name.
 */
export default function Root() {
  // The investor view is its own front door: no Supabase session, no app
  // chrome, and it must not require an account, since an account here is an
  // editor's account.
  if (typeof window !== "undefined" && window.location.pathname.replace(/\/$/, "") === "/investor") {
    return <InvestorView />;
  }

  // A scenario link is the same kind of front door: whoever it was sent to has
  // no account here and should not need one.
  const scenarioToken =
    typeof window !== "undefined" ? window.location.pathname.match(/^\/s\/([a-f0-9]{20,80})$/)?.[1] : null;
  if (scenarioToken) return <ScenarioView token={scenarioToken} />;

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);
  // A password an admin generated gets one use. Checked from the profile
  // rather than anything the browser holds, so it cannot be skipped by
  // reloading.
  const [mustSetPassword, setMustSetPassword] = useState(false);
  const [checkedProfile, setCheckedProfile] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Clicking a reset link creates a real session immediately, so without
      // this the app would just let you in and never ask for a new password —
      // and the reset link would silently become a way to skip the password.
      if (event === "PASSWORD_RECOVERY") setIsRecovery(true);
      if (event === "USER_UPDATED") setIsRecovery(false);
      // Only a real sign-in, not the token refreshes that follow it around.
      if (event === "SIGNED_IN" && s?.user) recordSignIn(s.user);
      setSession(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user?.id) {
      setCheckedProfile(true);
      return;
    }
    let mounted = true;
    setCheckedProfile(false);
    supabase
      .from("profiles")
      .select("must_change_password")
      .eq("id", session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!mounted) return;
        setMustSetPassword(!!data?.must_change_password);
        setCheckedProfile(true);
      });
    return () => {
      mounted = false;
    };
  }, [session?.user?.id]);

  if (!isSupabaseConfigured()) {
    return <Message>Backend not configured — VITE_SUPABASE_URL / ANON_KEY are missing.</Message>;
  }
  if (loading) return <Message>Loading session…</Message>;
  if (session && !checkedProfile) return <Message>Loading session…</Message>;
  if (session && mustSetPassword) {
    return (
      <SetPassword
        email={session.user?.email}
        onDone={async () => {
          setMustSetPassword(false);
        }}
      />
    );
  }
  // Recovery takes priority over the session it just created: hold the user on
  // the set-password screen until they actually set one.
  if (isRecovery) return <LoginScreen initialMode="set_password" />;
  if (!session) return <LoginScreen />;
  return <App session={session} />;
}
